import { generatePath } from 'react-router-dom';
import { DropdownMenu, Button, IClonedDropDownItemProps, Tooltip } from '@treezor/design-system';

import { useTranslation } from 'react-i18next';

import { useWalletList } from '../hooks';

import { useRoutepaths } from '@/router/hooks/useRoutePaths';
import { useProfileContext } from '@/features/context/ProfileContext';
import { TDashboardConfigSettingsCardsDesignType } from '@/store/models/dashboard-config.model';

export function CardUnitCreation() {
  const { user } = useProfileContext();
  const PathRoute = useRoutepaths();
  const { t } = useTranslation();

  const { designTypes, isLoading, hasUserInformation, isDisabled, createCard } = useWalletList(user);

  if (designTypes?.length === 0) return <></>;

  const CarCreationMenuItem = ({
    designType,
    onClose = undefined,
  }: IClonedDropDownItemProps & { designType: TDashboardConfigSettingsCardsDesignType }) => {
    return (
      <div
        className="flex flex-col text-secondary max-w-[240px] py-3 px-5 group cursor-pointer hover:bg-secondary"
        data-testid={`btn-create-card-${designType}`}
        onClick={() => {
          createCard(designType);
          onClose?.();
        }}
      >
        <span className="typo-body-bold-paragraph group-hover:text-interactive">
          {t(`pages.currentProfiles.cards.card.createCard.${designType}.title`)}
        </span>
        <span className="typo-body-regular-small">
          {t(`pages.currentProfiles.cards.card.createCard.${designType}.description`)}
        </span>
      </div>
    );
  };

  const createButton = (
    <>
      {designTypes?.length === 1 ? (
        <Button
          size="s"
          variant="secondary"
          disabled={isDisabled}
          isLoading={isLoading}
          testId="btn-create-card"
          onClick={() => createCard(designTypes[0])}
        >
          {t('pages.currentProfiles.cards.card.createCard.create')}
        </Button>
      ) : (
        <DropdownMenu
          size="s"
          closeOnSelect
          isLoading={isLoading}
          disabled={isDisabled}
          items={designTypes.map((designType) => ({
            component: <CarCreationMenuItem designType={designType} key={designType} />,
          }))}
        >
          {t('pages.currentProfiles.cards.card.createCard.create')}
        </DropdownMenu>
      )}
    </>
  );

  return (
    <>
      {isDisabled ? (
        <Tooltip
          messageContent={
            <div className="flex flex-col gap-3 items-start">
              {hasUserInformation ? (
                <>
                  <span>{t('pages.currentProfiles.cards.card.createCard.cardRequiredTooltip')}</span>
                  <Button
                    as="a"
                    size="s"
                    variant="ghost"
                    color="inverse"
                    icon="add"
                    className="text-onColor"
                    href={generatePath(PathRoute.app.users.tabs.wallets.main)}
                  >
                    {t('pages.currentProfiles.actionUser.addWallet.add')}
                  </Button>
                </>
              ) : (
                <>
                  <span>{t('pages.currentProfiles.cards.card.createCard.kycRequiredTooltip')}</span>
                  <Button
                    as="a"
                    size="s"
                    variant="ghost"
                    color="inverse"
                    icon="edit"
                    className="text-onColor"
                    href={generatePath(PathRoute.app.users.tabs.kyckyb)}
                  >
                    {t('pages.currentProfiles.cards.card.createCard.editUserInformation')}
                  </Button>
                </>
              )}
            </div>
          }
          placement="bottom"
          className="py-2"
          contentClassName="w-[320px] z-[1002] p-4 mt-0"
        >
          {createButton}
        </Tooltip>
      ) : (
        createButton
      )}
    </>
  );
}
